import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useI18n } from "../lib/i18n";
import ConfirmDeleteDialog from "../components/ConfirmDeleteDialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Building2, MapPinned, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = { tenant_id: "", name: "", deliverer_ids: [] };

function tr(t, key, vars = {}) {
  let text = t(key);
  for (const [name, value] of Object.entries(vars)) text = text.replace(`{${name}}`, String(value));
  return text;
}

function SectorFormDialog({ open, onOpenChange, mode, sector, tenantId, tenants, deliverers, t, onSaved }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [filter, setFilter] = useState("");
  const [saving, setSaving] = useState(false);
  const isEdit = mode === "edit";

  useEffect(() => {
    if (!open) return;
    setFilter("");
    if (isEdit && sector) {
      setForm({
        tenant_id: sector.tenant_id || "",
        name: sector.name || "",
        deliverer_ids: (sector.deliverers || []).map((deliverer) => deliverer.id),
      });
    } else {
      setForm({ ...EMPTY_FORM, tenant_id: tenantId || (tenants.length === 1 ? tenants[0].id : "") });
    }
  }, [open, isEdit, sector, tenantId, tenants]);

  const visibleDeliverers = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const rows = [...deliverers].sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
    if (!needle) return rows;
    return rows.filter((deliverer) => deliverer.name.toLowerCase().includes(needle) || (deliverer.email || "").toLowerCase().includes(needle));
  }, [deliverers, filter]);

  const toggleDeliverer = (id) => {
    setForm((current) => ({
      ...current,
      deliverer_ids: current.deliverer_ids.includes(id)
        ? current.deliverer_ids.filter((item) => item !== id)
        : [...current.deliverer_ids, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        name: form.name.trim(),
        deliverer_ids: form.deliverer_ids,
      };
      if (isEdit) {
        await api.patch(`/sectors/${sector.id}`, payload);
        toast.success(t("sectors_updated"));
      } else {
        await api.post("/sectors", { ...payload, tenant_id: form.tenant_id });
        toast.success(t("sectors_created"));
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("sectors_save_failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl max-w-md" data-testid={isEdit ? "sector-edit-dialog" : "sector-dialog"}>
        <DialogHeader>
          <DialogTitle className="font-display font-bold">
            {isEdit ? t("sectors_edit") : t("sectors_new")}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">{t("nav_tenants")}</label>
            <Select
              value={form.tenant_id}
              onValueChange={(value) => setForm({ ...form, tenant_id: value })}
              disabled={isEdit}
            >
              <SelectTrigger className="rounded-xl h-10 mt-1" data-testid="sector-input-tenant">
                <SelectValue placeholder={t("users_select_tenant")} />
              </SelectTrigger>
              <SelectContent>
                {tenants.map((tenant) => (
                  <SelectItem key={tenant.id} value={tenant.id}>{tenant.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[var(--text-2)]">{t("sectors_name")}</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder={t("sectors_name_placeholder")}
              required
              className="rounded-xl h-10 mt-1"
              data-testid="sector-input-name"
            />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="text-xs font-medium text-[var(--text-2)]">{t("sectors_deliverers")}</label>
              <span className="text-[10px] text-[var(--text-3)]">
                {tr(t, "sectors_selected", { count: form.deliverer_ids.length })}
              </span>
            </div>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t("sectors_filter_deliverers")}
              className="rounded-xl h-9 mt-1"
              data-testid="sector-deliverer-filter"
            />
            <div className="mt-1 max-h-52 overflow-y-auto rounded-xl border border-[var(--border-c)] bg-[var(--surface-2)] p-2 space-y-1" data-testid="sector-input-deliverers">
              {deliverers.length === 0 && (
                <div className="text-xs text-[var(--text-3)] px-1 py-2">{t("sectors_no_deliverers")}</div>
              )}
              {deliverers.length > 0 && visibleDeliverers.length === 0 && (
                <div className="text-xs text-[var(--text-3)] px-1 py-2">{t("sectors_no_match")}</div>
              )}
              {visibleDeliverers.map((deliverer) => (
                <label key={deliverer.id} className="flex items-center gap-2 text-sm cursor-pointer px-1 py-1 rounded-lg hover:bg-[var(--surface-hover)]">
                  <Checkbox
                    checked={form.deliverer_ids.includes(deliverer.id)}
                    onCheckedChange={() => toggleDeliverer(deliverer.id)}
                    data-testid={`sector-deliverer-${deliverer.id}`}
                  />
                  <span className="min-w-0 truncate">{deliverer.name}</span>
                  {deliverer.is_active === false && (
                    <span className="pill pill-neutral shrink-0">{t("users_inactive")}</span>
                  )}
                </label>
              ))}
            </div>
            <p className="text-[10px] text-[var(--text-3)] mt-1">{t("sectors_deliverer_hint")}</p>
          </div>
          <Button
            type="submit"
            disabled={saving || !form.tenant_id}
            className="w-full rounded-xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-11"
            data-testid="sector-submit"
          >
            {saving ? t("saving") : isEdit ? t("users_save") : t("sectors_create_btn")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Sectors() {
  const { t } = useI18n();
  const [tenants, setTenants] = useState([]);
  const [deliverers, setDeliverers] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [query, setQuery] = useState("");
  const [createTenantId, setCreateTenantId] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [editSector, setEditSector] = useState(null);
  const [deleteSector, setDeleteSector] = useState(null);

  const load = useCallback(async () => {
    const [tenantRes, userRes, sectorRes] = await Promise.all([
      api.get("/tenants"),
      api.get("/users", { params: { role: "deliverer" } }),
      api.get("/sectors"),
    ]);
    setTenants(tenantRes.data || []);
    setDeliverers(userRes.data || []);
    setSectors(sectorRes.data || []);
  }, []);

  useEffect(() => {
    void load().catch(() => toast.error(t("sectors_save_failed")));
  }, [load, t]);

  const closeCreate = () => {
    setCreateOpen(false);
    setCreateTenantId("");
  };

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const byTenant = new Map();
    for (const sector of sectors) {
      const list = byTenant.get(sector.tenant_id) || [];
      list.push(sector);
      byTenant.set(sector.tenant_id, list);
    }

    return [...tenants]
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"))
      .map((tenant) => {
        const rows = (byTenant.get(tenant.id) || []).slice().sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
        if (!needle) return { tenant, sectors: rows };
        const tenantHit = tenant.name.toLowerCase().includes(needle) || (tenant.city || "").toLowerCase().includes(needle);
        if (tenantHit) return { tenant, sectors: rows };
        const matched = rows.filter((sector) => (
          sector.name.toLowerCase().includes(needle)
          || (sector.deliverers || []).some((deliverer) => deliverer.name.toLowerCase().includes(needle))
        ));
        return matched.length ? { tenant, sectors: matched } : null;
      })
      .filter(Boolean);
  }, [tenants, sectors, query]);

  const confirmDelete = async () => {
    if (!deleteSector) return;
    try {
      await api.delete(`/sectors/${deleteSector.id}`);
      toast.success(t("sectors_deleted"));
      setDeleteSector(null);
      await load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail) || t("sectors_save_failed"));
      throw err;
    }
  };

  return (
    <div className="space-y-4">
      <div className="card-soft px-5 py-4 flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs text-[var(--text-2)] font-medium">{t("section_governance")}</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-0.5">{t("nav_sectors")}</h1>
          <p className="text-xs text-[var(--text-2)] mt-1">{t("sectors_subtitle")}</p>
        </div>
        <Button
          onClick={() => { setCreateTenantId(""); setCreateOpen(true); }}
          disabled={tenants.length === 0}
          className="rounded-2xl bg-[var(--brand)] hover:bg-[var(--brand-hover)] h-10 text-xs font-semibold px-4"
          data-testid="new-sector-btn"
        >
          <Plus size={14} className="mr-1" /> {t("sectors_new")}
        </Button>
      </div>

      <div className="card-soft px-4 py-3 flex items-center gap-2">
        <Search size={16} className="text-[var(--text-3)] shrink-0" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t("sectors_search")}
          className="border-0 shadow-none h-9 px-0 focus-visible:ring-0"
          data-testid="sector-search"
        />
      </div>

      {tenants.length === 0 && (
        <div className="card-soft p-8 text-center text-sm text-[var(--text-2)]">{t("sectors_no_tenants")}</div>
      )}

      {tenants.length > 0 && groups.length === 0 && (
        <div className="card-soft p-8 text-center text-sm text-[var(--text-2)]">{t("sectors_no_match")}</div>
      )}

      <div className="space-y-4">
        {groups.map(({ tenant, sectors: rows }) => (
          <section key={tenant.id} className="card-soft p-5" data-testid={`sector-tenant-${tenant.id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-11 h-11 rounded-2xl bg-[var(--brand-soft)] text-[var(--brand)] flex items-center justify-center shrink-0">
                  <Building2 size={20} />
                </div>
                <div className="min-w-0">
                  <h2 className="font-display text-lg font-bold tracking-tight truncate">{tenant.name}</h2>
                  <p className="text-xs text-[var(--text-2)] mt-0.5">
                    {tenant.city ? `${tenant.city} · ` : ""}
                    {tr(t, "sectors_count", { count: rows.length })}
                  </p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl h-9 text-xs"
                onClick={() => { setCreateTenantId(tenant.id); setCreateOpen(true); }}
                data-testid={`sector-add-${tenant.id}`}
              >
                <Plus size={14} className="mr-1" /> {t("sectors_add")}
              </Button>
            </div>

            {rows.length === 0 ? (
              <p className="text-sm text-[var(--text-3)] mt-4">{t("sectors_empty")}</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                {rows.map((sector) => (
                  <article key={sector.id} className="rounded-2xl border border-[var(--border-c)] p-4" data-testid={`sector-${sector.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <MapPinned size={16} className="text-[var(--brand)] shrink-0" />
                        <h3 className="font-semibold truncate">{sector.name}</h3>
                      </div>
                      <div className="flex items-center shrink-0">
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-xl"
                          onClick={() => setEditSector(sector)}
                          aria-label={t("sectors_edit")}
                          data-testid={`sector-edit-${sector.id}`}
                        >
                          <Pencil size={15} />
                        </Button>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 rounded-xl text-destructive"
                          onClick={() => setDeleteSector(sector)}
                          aria-label={t("sectors_delete")}
                          data-testid={`sector-delete-${sector.id}`}
                        >
                          <Trash2 size={15} />
                        </Button>
                      </div>
                    </div>
                    {(sector.deliverers || []).length === 0 ? (
                      <p className="text-xs text-[var(--text-3)] mt-3">{t("sectors_none_linked")}</p>
                    ) : (
                      <ul className="mt-3 space-y-1.5">
                        {(sector.deliverers || []).map((deliverer) => (
                          <li key={deliverer.id} className="text-sm flex items-center gap-2">
                            <span className="w-1.5 h-1.5 rounded-full bg-[var(--brand)] shrink-0" />
                            <span className="truncate">{deliverer.name}</span>
                            {deliverer.is_active === false && (
                              <span className="pill pill-neutral shrink-0">{t("users_inactive")}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </article>
                ))}
              </div>
            )}
          </section>
        ))}
      </div>

      <SectorFormDialog
        open={createOpen}
        onOpenChange={(open) => { if (!open) closeCreate(); else setCreateOpen(true); }}
        mode="create"
        tenantId={createTenantId}
        tenants={tenants}
        deliverers={deliverers}
        t={t}
        onSaved={() => { closeCreate(); void load(); }}
      />
      <SectorFormDialog
        open={!!editSector}
        onOpenChange={(open) => { if (!open) setEditSector(null); }}
        mode="edit"
        sector={editSector}
        tenants={tenants}
        deliverers={deliverers}
        t={t}
        onSaved={() => { setEditSector(null); void load(); }}
      />
      <ConfirmDeleteDialog
        open={!!deleteSector}
        onOpenChange={(open) => { if (!open) setDeleteSector(null); }}
        onConfirm={confirmDelete}
        title={t("sectors_delete_title")}
        itemName={deleteSector?.name}
        description={t("sectors_delete_desc")}
        confirmLabel={t("sectors_delete")}
        cancelLabel={t("cancel")}
        testId="sector-delete-dialog"
      />
    </div>
  );
}
